import { useMemo } from 'react';

import Input from 'src/components/Input';
import styles from './PasswordInput.module.scss';

export type PasswordRule = {
  key: string;
  label: string;
  test: (v: string) => boolean;
};

export const PASSWORD_RULES: PasswordRule[] = [
  { key: 'minLength', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { key: 'uppercase', label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { key: 'lowercase', label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { key: 'number', label: 'One number', test: (v) => /\d/.test(v) },
  { key: 'special', label: 'One special character', test: (v) => /[!@#$%^&*(),.?":{}|<>]/.test(v) },
];

type Props = {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  variant?: 'primary' | 'secondary';
  showRules?: boolean;
};

const PasswordInput = ({
  label = 'Password',
  value,
  onChange,
  error,
  variant = 'secondary',
  showRules = true,
}: Props) => {
  const passwordChecks = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ ...rule, passed: rule.test(value) })),
    [value],
  );

  return (
    <div>
      <Input
        type="password"
        label={label}
        variant={variant}
        value={value}
        onChange={onChange}
        error={error}
      />
      {showRules && (
        <div className={styles.passwordRequirements}>
          {passwordChecks.map((rule) => (
            <div
              key={rule.key}
              className={`${styles.passwordRule} ${rule.passed ? styles.passwordRulePassed : ''}`}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                {rule.passed ? (
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ) : (
                  <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
                )}
              </svg>
              <span>{rule.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
